import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const EMPTY_FORM = {
  name: "",
  description: "",
  goal: "maintenance",
  daily_calories: "",
  daily_protein_g: "",
  daily_carbs_g: "",
  daily_fat_g: "",
  meals: [],
  tags: [],
};

export default function MealPlanTemplateForm({ open, onOpenChange, initialData, onSuccess }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (open) {
      setForm(
        initialData
          ? {
              ...initialData,
              daily_calories: initialData.daily_calories ?? "",
              daily_protein_g: initialData.daily_protein_g ?? "",
              daily_carbs_g: initialData.daily_carbs_g ?? "",
              daily_fat_g: initialData.daily_fat_g ?? "",
              tags: initialData.tags || [],
            }
          : EMPTY_FORM
      );
      setTagInput("");
    }
  }, [open, initialData]);

  const saveMutation = useMutation({
    mutationFn: (data) =>
      initialData
        ? base44.entities.MealPlanTemplate.update(initialData.id, data)
        : base44.entities.MealPlanTemplate.create(data),
    onSuccess,
  });

  const addMeal = () =>
    setForm((f) => ({ ...f, meals: [...f.meals, { meal_name: "", time: "", foods: [] }] }));

  const updateMeal = (idx, field, value) => {
    const meals = [...form.meals];
    meals[idx] = { ...meals[idx], [field]: value };
    setForm((f) => ({ ...f, meals }));
  };

  const removeMeal = (idx) =>
    setForm((f) => ({ ...f, meals: f.meals.filter((_, i) => i !== idx) }));

  const addFood = (mealIdx) => {
    const meals = [...form.meals];
    meals[mealIdx].foods = [
      ...(meals[mealIdx].foods || []),
      { name: "", portion: "", calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
    ];
    setForm((f) => ({ ...f, meals }));
  };

  const updateFood = (mealIdx, foodIdx, field, value) => {
    const meals = [...form.meals];
    meals[mealIdx].foods[foodIdx] = { ...meals[mealIdx].foods[foodIdx], [field]: value };
    setForm((f) => ({ ...f, meals }));
  };

  const removeFood = (mealIdx, foodIdx) => {
    const meals = [...form.meals];
    meals[mealIdx].foods = meals[mealIdx].foods.filter((_, i) => i !== foodIdx);
    setForm((f) => ({ ...f, meals }));
  };

  const addTag = (e) => {
    if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim().replace(/,/g, "");
      if (!form.tags.includes(tag)) {
        setForm((f) => ({ ...f, tags: [...f.tags, tag] }));
      }
      setTagInput("");
    }
  };

  const removeTag = (tag) =>
    setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }));

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate({
      ...form,
      daily_calories: form.daily_calories ? parseFloat(form.daily_calories) : undefined,
      daily_protein_g: form.daily_protein_g ? parseFloat(form.daily_protein_g) : undefined,
      daily_carbs_g: form.daily_carbs_g ? parseFloat(form.daily_carbs_g) : undefined,
      daily_fat_g: form.daily_fat_g ? parseFloat(form.daily_fat_g) : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {initialData ? "Edit Template" : "Create Meal Plan Template"}
          </DialogTitle>
        </DialogHeader>
        <HealthSourceNote topic="planning" />

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Template Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. High-Protein Cutting Plan"
                required
              />
            </div>
            <div>
              <Label>Goal</Label>
              <Select value={form.goal} onValueChange={(v) => setForm({ ...form, goal: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bulking">Bulking</SelectItem>
                  <SelectItem value="cutting">Cutting</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="clean_eating">Clean Eating</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Daily Calories</Label>
              <Input
                type="number"
                value={form.daily_calories}
                onChange={(e) => setForm({ ...form, daily_calories: e.target.value })}
                placeholder="2000"
              />
            </div>
          </div>

          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Daily Macro Targets (g)</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Protein</Label>
              <Input type="number" value={form.daily_protein_g} onChange={(e) => setForm({ ...form, daily_protein_g: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Carbs</Label>
              <Input type="number" value={form.daily_carbs_g} onChange={(e) => setForm({ ...form, daily_carbs_g: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Fat</Label>
              <Input type="number" value={form.daily_fat_g} onChange={(e) => setForm({ ...form, daily_fat_g: e.target.value })} />
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Brief description of this template..."
            />
          </div>

          {/* Tags */}
          <div>
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1 text-xs">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={addTag}
              placeholder="Add tag and press Enter (e.g. vegetarian, high-protein)"
              className="text-sm"
            />
          </div>

          {/* Meals */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-display">Meals</Label>
              <Button type="button" variant="outline" size="sm" onClick={addMeal} className="gap-1">
                <Plus className="w-3 h-3" /> Add Meal
              </Button>
            </div>
            {form.meals.map((meal, mi) => (
              <div key={mi} className="p-3 rounded-xl bg-muted/50 space-y-2">
                <div className="flex gap-2">
                  <Input
                    className="flex-1"
                    value={meal.meal_name}
                    onChange={(e) => updateMeal(mi, "meal_name", e.target.value)}
                    placeholder="Meal name (e.g. Breakfast)"
                  />
                  <Input
                    className="w-24"
                    value={meal.time}
                    onChange={(e) => updateMeal(mi, "time", e.target.value)}
                    placeholder="07:00"
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeMeal(mi)} className="text-destructive shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="pl-3 space-y-1.5">
                  {(meal.foods || []).map((food, fi) => (
                    <div key={fi} className="flex gap-2 items-center">
                      <Input
                        className="flex-1"
                        value={food.name}
                        onChange={(e) => updateFood(mi, fi, "name", e.target.value)}
                        placeholder="Food"
                      />
                      <Input
                        className="w-20"
                        value={food.portion}
                        onChange={(e) => updateFood(mi, fi, "portion", e.target.value)}
                        placeholder="Portion"
                      />
                      <Input
                        className="w-16"
                        type="number"
                        value={food.calories}
                        onChange={(e) => updateFood(mi, fi, "calories", parseInt(e.target.value) || 0)}
                        placeholder="Cal"
                      />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeFood(mi, fi)} className="text-muted-foreground shrink-0">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="ghost" size="sm" onClick={() => addFood(mi)} className="text-xs gap-1 text-muted-foreground">
                    <Plus className="w-3 h-3" /> Add Food
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Button type="submit" className="w-full" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "Saving..." : initialData ? "Update Template" : "Create Template"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}